import { Query } from '@tanstack/react-query';
import { UseDataQuery } from '../../../hooks/useDataQuery/dataQuery.types';
import { OmitPaginatedEndpointParams } from '../../types';
import {
  ORDER_TARGETS_CACHE_KEY,
  TARGETS_LIST_CACHE_KEY,
  TARGETS_MAX_CONVERSION_CACHE_KEY,
  TARGETS_METHOD_OPTIONS_CACHE_KEY,
  TARGETS_STATUS_GROUP_COUNTS_CACHE_KEY,
  TARGETS_TOTALS_CACHE_KEY,
  TARGET_BREAKDOWNS_CACHE_KEY,
  TARGET_DETAIL_CACHE_KEY,
  TARGET_DIMENSIONS_CACHE_KEY,
} from './target.repository';
import {
  FindBreakdownItemsQuery,
  FindTargetMaxConversionParams,
  FindTargetMethodOptionsParams,
  FindTargetStatusGroupCountsParams,
  FindTargetTotalsParams,
  FindTargetsParams,
  TargetBreakdownsFilter,
} from './target.repository.types';

const FILTER_DEPENDENT_CACHE_KEYS = [
  TARGETS_LIST_CACHE_KEY,
  TARGETS_STATUS_GROUP_COUNTS_CACHE_KEY,
  TARGETS_TOTALS_CACHE_KEY,
];

export const isFilterDependentTargetQuery = ({ queryKey }: Pick<Query, 'queryKey'>) =>
  FILTER_DEPENDENT_CACHE_KEYS.some((cacheKey) => queryKey.includes(cacheKey));

export const getInfiniteTargetsKey = (
  params?: OmitPaginatedEndpointParams<FindTargetsParams>,
): UseDataQuery['queryKey'] => [TARGETS_LIST_CACHE_KEY, undefined, params ?? {}];

export const getOrderTargetsKey = (orderId: string): UseDataQuery['queryKey'] => [
  ORDER_TARGETS_CACHE_KEY,
  orderId,
];

export const getFindBreakdownDimensionsKey = (): UseDataQuery['queryKey'] => [
  'targets/breakdown-dimensions',
];

export const getFindBreakdownItemsKey = (
  params: FindBreakdownItemsQuery,
): UseDataQuery['queryKey'] => ['targets/breakdown-items', undefined, params];

export const getTargetStatusGroupCountsKey = (
  params?: FindTargetStatusGroupCountsParams,
): UseDataQuery['queryKey'] => [TARGETS_STATUS_GROUP_COUNTS_CACHE_KEY, undefined, params ?? {}];

export const getTargetMaxConversionKey = (
  params: FindTargetMaxConversionParams,
): UseDataQuery['queryKey'] => [TARGETS_MAX_CONVERSION_CACHE_KEY, undefined, params];

export const getTargetTotalsKey = (params: FindTargetTotalsParams): UseDataQuery['queryKey'] => [
  TARGETS_TOTALS_CACHE_KEY,
  undefined,
  params,
];

export const getTargetMethodOptionsKey = (
  params: FindTargetMethodOptionsParams,
): UseDataQuery['queryKey'] => [TARGETS_METHOD_OPTIONS_CACHE_KEY, undefined, params];

export const getTargetDetailKey = (targetId: string): UseDataQuery['queryKey'] => [
  TARGET_DETAIL_CACHE_KEY,
  targetId,
];

export const getTargetBreakdownsKey = (
  targetId: string,
  filter: TargetBreakdownsFilter = {},
): UseDataQuery['queryKey'] => {
  const treeKey: UseDataQuery['queryKey'] = [TARGET_BREAKDOWNS_CACHE_KEY, targetId];
  const appliedFilter = Object.fromEntries(
    Object.entries(filter).filter(([, value]) => value !== undefined),
  );

  return Object.keys(appliedFilter).length > 0 ? [...treeKey, appliedFilter] : treeKey;
};

export const getTargetDimensionsKey = (targetId: string): UseDataQuery['queryKey'] => [
  TARGET_DIMENSIONS_CACHE_KEY,
  targetId,
];
